import React, { useState, useContext, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { GlobalContext } from '../../../../../../App.tsx';
import { DEFAULT_VOICE } from "../../../../../../Globals.ts";
import { useAppStore } from "../../../../../../GlobalStore/GlobalStore.ts";
import voiceMapping from "../../../../../../Utils/IdMappings/VoiceMapping.ts";
import {handleAudioClick} from "../../../../../../Utils/misc/playAudio.ts";

interface DropdownProps {
    options: string[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    allowEmpty?: boolean;
    isVoiceDropdown?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({ options, value, onChange, placeholder, allowEmpty, isVoiceDropdown }) => {
    const { miscMidRenderRefs, websocketRef, setupWebsocketHelper } = useContext(GlobalContext);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<any>(null);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const handleSoundButtonClick = () => {
        handleAudioClick(
            websocketRef,
            'test-voice',
            "Hello, how may I assist you today?",
            setDynamicStateObj,
            miscMidRenderRefs,
            voiceMapping[settingsObj.voice],
            setupWebsocketHelper
        );
    };

    const handleChange = (newValue: string) => {
        onChange(newValue);
        setIsOpen(false);

        if (isVoiceDropdown) {
            if (newValue.trim() === "") newValue = DEFAULT_VOICE;
            setSettingsObj({voice: newValue});
        }
    };

    return (
        <div className="flex items-center space-x-2 text-sm" ref={dropdownRef}>
            {isVoiceDropdown && (
                <button
                    onClick={handleSoundButtonClick}
                    className="bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200 rounded-lg p-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors duration-200"
                    title="Test voice"
                >
                    <FontAwesomeIcon icon={faVolumeUp} />
                </button>
            )}
            <div className="relative">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200 rounded-lg p-2 w-32 flex justify-between items-center text-sm"
                >
                    <span>{value || placeholder || 'Select...'}</span>
                    <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {isOpen && (
                    <div
                        className="z-10 absolute bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200 w-full mt-1 border border-neutral-600 rounded-lg shadow-lg overflow-y-auto max-h-52">
                        {allowEmpty && (
                            <div
                                className="px-4 py-2 cursor-pointer text-sm"
                                onClick={() => handleChange('')}
                            >
                                {placeholder || 'Select...'}
                            </div>
                        )}
                        {options.map((option) => (
                            <div
                                key={option}
                                className="px-4 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer text-sm"
                                onClick={() => handleChange(option)}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dropdown;
